import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { favoriteFiltersService } from '@/services/favoriteFiltersService';
import { PoolFilters } from '@/types/pool';

// Query keys
export const favoriteFilterKeys = {
  all: ['favoriteFilters'] as const,
  list: () => [...favoriteFilterKeys.all, 'list'] as const,
};

/**
 * Get list of saved favorite filters for the current user
 */
export function useFavoriteFilters() {
  return useQuery({
    queryKey: favoriteFilterKeys.list(),
    queryFn: async () => {
      const result = await favoriteFiltersService.getFavoriteFilters();
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    gcTime: 0,
    staleTime: 0,
  });
}

/**
 * Create a new favorite filter
 */
export function useCreateFavoriteFilter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { name: string; filters: PoolFilters; notifications_enabled: boolean }) => {
      const result = await favoriteFiltersService.createFavoriteFilter(
        payload.name,
        payload.filters,
        payload.notifications_enabled
      );
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: favoriteFilterKeys.list() });
    },
  });
}

/**
 * Update an existing favorite filter
 */
export function useUpdateFavoriteFilter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { filterId: number; name: string; filters: PoolFilters }) => {
      const result = await favoriteFiltersService.updateFavoriteFilter(payload.filterId, payload.name, payload.filters);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: favoriteFilterKeys.list() });
    },
  });
}

/**
 * Delete a favorite filter
 */
export function useDeleteFavoriteFilter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (filterId: number) => {
      const result = await favoriteFiltersService.deleteFavoriteFilter(filterId);
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: favoriteFilterKeys.list() });
    },
  });
}
